import { z } from 'zod';
import DOMPurify from 'dompurify';

export const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const HOURS = [
  '00:00-01:00', '01:00-02:00', '02:00-03:00', '03:00-04:00', '04:00-05:00',
  '05:00-06:00', '06:00-07:00', '07:00-08:00', '08:00-09:00', '09:00-10:00',
  '10:00-11:00', '11:00-12:00', '12:00-13:00', '13:00-14:00', '14:00-15:00',
  '15:00-16:00', '16:00-17:00', '17:00-18:00', '18:00-19:00', '19:00-20:00',
  '20:00-21:00', '21:00-22:00', '22:00-23:00', '23:00-00:00'
];

export const TaskSchema = z.object({
  id: z.string().max(50, 'Task ID too long'),
  content: z.string()
    .min(1, 'Task content is required')
    .max(500, 'Task content must be 500 characters or less')
    .transform(val => val.trim()),
  day: z.enum(DAYS),
  hour: z.string().max(20),
  duration: z.number().int().min(1).max(24).default(1),
});

export const TaskListSchema = z.array(TaskSchema).max(100, 'Maximum 100 tasks allowed');

export const NewTaskSchema = z.object({
  content: z.string()
    .min(1, 'Task content is required')
    .max(500, 'Task content must be 500 characters or less')
    .transform(val => val.trim()),
  day: z.enum(DAYS),
  hour: z.string(),
  duration: z.number().int().min(1).max(4).default(1),
});

export const EmailSchema = z.string()
  .email('Please enter a valid email address')
  .max(254, 'Email must be 254 characters or less')
  .transform(val => val.toLowerCase().trim());

export const PasswordSchema = z.string()
  .min(8, 'Password must be at least 8 characters long')
  .max(128, 'Password must be 128 characters or less')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number');

export const TimeRangeSchema = z.object({
  start: z.string(),
  end: z.string(),
});

export const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
export const MAX_FILE_SIZE = 2 * 1024 * 1024;

export const FileUploadSchema = z.object({
  type: z.enum(ALLOWED_IMAGE_TYPES, {
    errorMap: () => ({ message: 'Please select a valid image file (PNG, JPG, JPEG, GIF, WEBP)' }),
  }),
  size: z.number().max(MAX_FILE_SIZE, 'Image size should be less than 2MB'),
});

export function sanitizeTaskContent(content) {
  return content
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function validateTask(task) {
  return TaskSchema.safeParse(task);
}

export function validateNewTask(task) {
  return NewTaskSchema.safeParse(task);
}

export function validateEmail(email) {
  return EmailSchema.safeParse(email);
}

export function validatePassword(password) {
  return PasswordSchema.safeParse(password);
}

export function validateFileUpload(file) {
  const result = FileUploadSchema.safeParse({
    type: file.type,
    size: file.size,
  });
  
  if (!result.success) {
    return { success: false, error: result.error.errors[0]?.message || 'Invalid file' };
  }
  
  return { success: true };
}

const rateLimitStore = new Map();

export function checkRateLimit(
  key,
  maxAttempts = 5,
  windowMs = 60000
) {
  const now = Date.now();
  const record = rateLimitStore.get(key);
  
  if (!record || now > record.resetTime) {
    rateLimitStore.set(key, { count: 1, resetTime: now + windowMs });
    return { allowed: true, remainingAttempts: maxAttempts - 1, resetTime: now + windowMs };
  }
  
  if (record.count >= maxAttempts) {
    return { allowed: false, remainingAttempts: 0, resetTime: record.resetTime };
  }
  
  record.count++;
  return { allowed: true, remainingAttempts: maxAttempts - record.count, resetTime: record.resetTime };
}

export function clearRateLimit(key) {
  rateLimitStore.delete(key);
}

export function sanitizeHtml(input) {
  return DOMPurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
}

export function sanitizeForDisplay(input) {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
}

export function generateSecureId() {
  const array = new Uint32Array(4);
  crypto.getRandomValues(array);
  return Array.from(array, (n) => n.toString(36)).join('');
}

export function isSecureContext() {
  return window.isSecureContext || window.location.protocol === 'https:' || window.location.hostname === 'localhost';
}

export const safeLocalStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Silently fail
    }
  },
};

const AUTH_ERROR_MESSAGES = {
  'auth/invalid-credential': 'Invalid credentials. Please try again.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/email-already-in-use': 'Unable to create account. Please try a different email.',
  'auth/weak-password': 'Password does not meet security requirements.',
  'auth/too-many-requests': 'Too many attempts. Please try again later.',
  'auth/user-disabled': 'This account has been disabled.',
  'auth/network-request-failed': 'Network error. Please check your connection.',
  'auth/popup-closed-by-user': 'Authentication was cancelled.',
  'auth/user-cancelled': 'Authentication was cancelled.',
  default: 'Authentication failed. Please try again.',
};

export function getSafeAuthErrorMessage(errorCode) {
  return AUTH_ERROR_MESSAGES[errorCode] || AUTH_ERROR_MESSAGES.default;
}

export function validateMagicBytes(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const arr = new Uint8Array(reader.result);
      if (arr.length < 4) {
        resolve(false);
        return;
      }
      
      const magicNumbers = {
        'image/png': [[0x89, 0x50, 0x4E, 0x47]],
        'image/jpeg': [[0xFF, 0xD8, 0xFF]],
        'image/gif': [[0x47, 0x49, 0x46, 0x38]],
        'image/webp': [[0x52, 0x49, 0x46, 0x46]],
      };
      
      const fileType = file.type;
      const validMagics = magicNumbers[fileType];
      
      if (!validMagics) {
        resolve(true);
        return;
      }
      
      const isValid = validMagics.some((magic) =>
        magic.every((byte, index) => arr[index] === byte)
      );
      
      resolve(isValid);
    };
    
    reader.onerror = () => resolve(false);
    reader.readAsArrayBuffer(file.slice(0, 16));
  });
}

export function validateFileExtension(filename, allowedExtensions) {
  const ext = filename.toLowerCase().split('.').pop();
  return ext ? allowedExtensions.includes(ext) : false;
}

export const ALLOWED_FILE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp'];
